import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { addWeeks, format, addDays } from 'date-fns';
import { de } from 'date-fns/locale';

import { getMondayOfWeek } from '../../../../core/utils/date';
import { RootState } from '../../../../core/state/state';
import { Actions } from '../../../../core/state/bookings/actions';

import Icon from '../../../../components/icon/Icon';
import Button from '../../../../components/form/button/Button';
import { isMobileScreenSize } from '../../../../core/utils/isMobileScreenSize';
import { selectBlockedSlotsByDate } from '../../../../core/state/blocked-slots/selectors';

import './DateSelector.scss';

const DateSelector: React.FC = () => {
  const selectedDate = useSelector((state: RootState) => state.bookings.selectedDate);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isMobileScreen = isMobileScreenSize();
  const language = useSelector((state: RootState) => state.user.userInfo?.lang);
  const blockedSlot = useSelector((state: RootState) => selectBlockedSlotsByDate(state, selectedDate))[0];
  const deliveryZones = useSelector((state: RootState) => state.zones.deliveryZones)?.filter((item) => item.active);

  const previousWeek = useCallback(() => dispatch(Actions.changeDate(addWeeks(selectedDate, -1))), [dispatch, selectedDate]);
  const nextWeek = useCallback(() => dispatch(Actions.changeDate(addWeeks(selectedDate, 1))), [dispatch, selectedDate]);
  const previousDay = useCallback(() => dispatch(Actions.changeDate(addDays(selectedDate, -1))), [dispatch, selectedDate]);
  const nextDay = useCallback(() => dispatch(Actions.changeDate(addDays(selectedDate, 1))), [dispatch, selectedDate]);
  const thisWeek = useCallback(() => dispatch(Actions.changeDate(getMondayOfWeek())), [dispatch]);

  return (
    <div className={`date-selector ${isMobileScreen && 'date-selector--mobile'}`}>
      <h3
        className={`date-selector__date 
          ${blockedSlot?.blockedAllDay && blockedSlot?.zones?.length === deliveryZones?.length && 'date-selector__date--disabled'}`}
      >
        {isMobileScreen
          ? format(selectedDate, 'EEE dd.LL.yyyy.', language === 'de-DE' ? { locale: de } : undefined)
          : format(selectedDate, 'MMMM y', language === 'de-DE' ? { locale: de } : undefined)}
      </h3>
      <Icon
        className="date-selector__previous button"
        icon="arrow"
        onClick={isMobileScreen ? previousDay : previousWeek}
        size="extra-small"
        isButton
        withCard
        svg
      />
      <Icon
        className="date-selector__next button"
        icon="arrow"
        onClick={isMobileScreen ? nextDay : nextWeek}
        size="extra-small"
        isButton
        withCard
        svg
      />
      <Button className="date-selector__day font-weight-normal" onClick={thisWeek}>
        {t('common.today')}
      </Button>
    </div>
  );
};

export default DateSelector;
